import { isEmpty, get as LodashGet } from 'lodash';
import { create } from 'zustand';
import { Model, ModelDetails, ModelService, ProtocolDetail, MODELS_PAGE_SIZE } from '../containers/my-central/services';
import { Tracker } from '../shared/tracker/Tracker';
import { usePrescriptionStore } from './prescriptionStore';
import { saveProtocolData } from '../shared/amplitude/amplitudeEventStorage';
import { resolveDoctorSpeciality } from '../shared/bannerApi';
import { amplitudeModule } from '../shared/amplitude';
import type { ProtocolEventProtocol, PrescriptionCompletedProtocol } from '../shared/amplitude/types';
import { getMedicalCertificateLabel } from '../containers/my-central/Models/medicalCertificateLabel';
import { Medicine, ManualMedicine } from '../interfaces/medicine';
import { Cid, Exam, ExamSearch } from '../interfaces/exam';
import { MedicalCertificate } from '../interfaces/medicalCertificate';
import { MedicalReferral } from '../interfaces/MedicalReferral';
import { MedicalReport } from '../interfaces/medicalReport';
import { Formula } from '../interfaces/formula';
import { PrescriptionService } from '../containers/prescription/service';
import { TrackerEventService } from '../containers/prescription/trackerEventService';
import { FormulaService } from '../components/search/formulaService';
import { logger } from '../helpers/logger';
import { toast } from '../helpers/toast';
import { isMobileWeb } from '../helpers/isMobileWeb';
import { resolveGenericLabInfo } from './helpers/genericLabPreference';
import { GENERIC_LAB_CLICK_EVENT, trackGenericLabEvent } from './helpers/genericLabEvent';
import { PatientService } from '../containers/my-central/patientService';
import { useInteractionStore } from './interactionStore';
import { ExamService } from '../components/search/examService';
import { useExamStore } from './examStore';
import {
  collectProtocolCidCodes,
  isCatalogMedication,
  protocolCertificateToDTO,
  protocolExamItemCidCodes,
  protocolExamToExams,
  protocolMedicationToManualMedicine,
  protocolMedicationToMedicine,
  protocolReferralToMedicalReferral,
  protocolReportToDTO,
  resolveProtocolGeneralExamCid,
  resolveProtocolGeneralExamObservation,
} from '../containers/my-central/Models/protocolMapping';
export enum FILTER_TYPES {
  ALL = 'all',
  PERSONAL = 'personal',
  INSTITUTIONAL = 'institutional',
  HIGHLIGHTED = 'highlighted',
}

export interface GroupedModels {
  all: Model[];
  personal: Model[];
  institutional: Model[];
  highlighted: Model[];
}

interface ModelsLength {
  all: number;
  personal: number;
  institutional: number;
  highlighted: number;
}

export type ModelFeedback =
  | {
      type: 'success';
      modelName: string;
      /** CIDs do modelo que não foram encontrados no catálogo. */
      unresolvedCidCount?: number;
      /** Exames do modelo que entraram sem vínculo com o catálogo. */
      unlinkedExamCount?: number;
      /** Exames que exigem lateralidade e entraram sem ela — bloqueiam a emissão. */
      missingLateralityCount?: number;
      /** Rótulos dos itens que falharam ao ser adicionados. */
      failedItems?: string[];
    }
  | { type: 'error'; modelName: string }
  | { type: 'saved'; modelName: string; unsavedFormulas?: string[] }
  | { type: 'removed'; modelName: string }
  | null;

export interface AppliedItemsSnapshot {
  prescriptionId: number;
  medicineIds: number[];
  manualMedicineIds: number[];
  examIds: number[];
  certificateIds: number[];
  referralIds: number[];
  reportIds: number[];
  formulaIds: number[];
  previousObservation: string | null;
}

export interface FetchModelsOptions {
  /**
   * Ignora a requisição equivalente em voo e dispara uma nova.
   *
   * Usado depois de criar/editar/excluir um modelo: a listagem que já estava em voo foi
   * pedida antes da mutação e devolveria a lista desatualizada.
   */
  force?: boolean;
}

export interface ModelStore {
  models: Model[];
  /**
   * Patrocinados conhecidos até agora.
   *
   * Com `enablePharmaIndustryModel` a listagem já sai com `includeSponsoredModels=true`, então
   * eles vêm junto de `models` e esta lista é **derivada** das páginas já carregadas — cresce a
   * cada `loadMoreModels`. Com a flag desligada ela vem do endpoint dedicado.
   *
   * Em nenhum dos dois casos é "todos os patrocinados do parceiro": o endpoint dedicado também
   * é paginado pelo default do backend. Para contar impressão use o que está **renderizado**
   * (o grupo `highlighted`), não esta lista.
   */
  sponsoredModels: Model[];
  /**
   * Se `sponsoredModels` foi de fato preenchida por uma resposta da listagem.
   *
   * É o que separa "a listagem trouxe os patrocinados" de "a listagem apenas começou": o
   * `modelsRequestId` sobe antes da requisição sair e continua alto quando ela falha, então
   * usá-lo como prova de entrega deixaria a pill "Sugestão" vazia pelo resto da sessão depois
   * de um GET /protocols com erro. Ver `fetchSponsoredModels`.
   */
  sponsoredModelsFromListing: boolean;
  sponsoredModelDetails: ModelDetails[];
  fetchModels(
    prescriberId: number,
    prescriptionId: number,
    age?: string,
    name?: string,
    options?: FetchModelsOptions,
  ): Promise<void>;
  fetchSponsoredModels(prescriberId: number, prescriptionId: number, age?: string): Promise<void>;
  modelsTotal: number;
  modelsPage: number;
  loadingMoreModels: boolean;
  modelsAge?: string;
  modelsName?: string;
  modelsSpeciality?: string[];
  modelsError: boolean;
  /**
   * Sequência da última busca disparada. Uma resposta só é aplicada se ainda for a mais
   * recente: duas buscas em voo podem voltar fora de ordem e deixar a lista divergente do
   * campo digitado.
   */
  modelsRequestId: number;
  /**
   * Se o prescritor administra os modelos institucionais do escopo em contexto.
   *
   * Vem junto da listagem (`GET /protocols`) porque a permissão depende do subparceiro, que
   * só é conhecido quando há prescrição em contexto — não é atributo do perfil do médico.
   */
  isInstitutionalModelAdmin: boolean;
  hasMoreModels(): boolean;
  loadMoreModels(prescriberId: number, prescriptionId: number): Promise<void>;
  filter: FILTER_TYPES;
  setFilter(filter: FILTER_TYPES): void;
  filteredModels: () => Model[];
  groupedModels: () => GroupedModels;
  modelsLength: () => ModelsLength;
  searchFilter: string;
  setSearchFilter(value: string): void;
  isCreatingModel: boolean;
  setIsCreatingModel(value: boolean): void;
  fetchSponsoredModelDetails(): Promise<void>;
  feedback: ModelFeedback;
  setFeedback(feedback: ModelFeedback): void;
  lastAppliedItems: AppliedItemsSnapshot | null;
  /**
   * `true` enquanto `undoLastApplication` está removendo os itens e restaurando a observação.
   *
   * A prescrição observa essa flag para não concorrer com o desfazer: a limpeza automática da
   * observação (quando o último item sai) dispararia no meio da remoção, escrevendo `null` em
   * cima da observação que o desfazer está restaurando.
   */
  isUndoingApplication: boolean;
  /**
   * Aplica um modelo à prescrição atual.
   *
   * Sem `selection`, todos os itens do modelo são adicionados; com ela, só os itens cujos
   * índices estão no Set correspondente (filtrando por tipo). `details` opcional evita um
   * refetch quando o chamador (ex.: `ApplyModelDrawer`) já carregou o `ProtocolDetail`.
   * `entryPoint` diz de onde o modelo foi adicionado e entra nos eventos de inserção.
   */
  applyModelToPrescription(
    model: Model,
    selection?: ApplySelection,
    details?: ModelDetails,
    entryPoint?: ModelEntryPoint
  ): Promise<void>;
  undoLastApplication(): Promise<void>;
}

/**
 * Seleção de quais itens do modelo aplicar à prescrição.
 *
 * Cada Set contém índices (0-based) dentro da coleção correspondente do `ProtocolDetail`.
 * Para `exam`, o índice é o do item dentro da lista plana (`flatExamItems`) do drawer.
 */
/**
 * Ponto da jornada de onde o modelo foi adicionado à prescrição.
 *
 * `tela_principal`: card da listagem na prescrição. `menu_lateral`: drawer "Ver modelos".
 * Vai nos eventos de inserção (NN-1461) para separar as duas origens na análise.
 */
export type ModelEntryPoint = 'tela_principal' | 'menu_lateral';

export interface ApplySelection {
  medication: Set<number>;
  exam: Set<number>;
  certificate: Set<number>;
  referral: Set<number>;
  report: Set<number>;
  compoundedFormula: Set<number>;
}

/**
 * Monta o item de protocolo do evento Amplitude `protocol_selected`.
 *
 * @param model Modelo aplicado.
 * @param modelDetails Detalhes do modelo, já carregados.
 * @param isEdited `true` quando o médico removeu itens do modelo ao aplicá-lo.
 * @param prescriberConselho Conselho do prescritor, usado para rotular atestados.
 * @param formulaNames Nomes das fórmulas manipuladas — `/protocols` só traz o id.
 * @returns O item de protocolo do evento.
 */
function buildProtocolEventProtocol(
  model: Model,
  modelDetails: ModelDetails,
  isEdited: boolean,
  prescriberConselho?: string | null,
  formulaNames?: Map<number, string>,
): ProtocolEventProtocol {
  const examItemsFlat = (modelDetails.exams ?? []).flatMap((group) => group.items ?? []);
  return {
    id: model.id,
    name: modelDetails.name,
    is_sponsored: model.sponsored || false,
    is_institutional: model.origin === 'institutional',
    created_at: modelDetails.createdAt ?? '',
    updated_at: modelDetails.updatedAt ?? '',
    is_edited: isEdited,
    medications: (modelDetails.medications ?? []).map((m) => ({
      id: m.id,
      name: m.brandName ?? m.activeIngredient ?? '',
      // O contrato /protocols não expõe patrocínio por medicamento dentro do modelo.
      is_sponsored: false,
    })),
    medical_tests: examItemsFlat.map((e) => ({
      id: e.id,
      name: e.description ?? '',
    })),
    doctors_notes: (modelDetails.certificates ?? []).map((a) => ({
      id: a.id,
      name: getMedicalCertificateLabel(a.type, prescriberConselho),
    })),
    referrals: (modelDetails.referrals ?? []).map((r) => ({
      id: r.id,
      name: r.specialty ?? '',
    })),
    medical_reports: (modelDetails.reports ?? []).map((r) => ({
      id: r.id,
      name: r.documentType ?? '',
    })),
    compounded_medications: (modelDetails.compoundedFormulas ?? []).map((f) => ({
      id: f.id,
      name: formulaNames?.get(f.formulaId) ?? '',
    })),
  };
}

/**
 * Monta o registro persistido em `saveProtocolData`, que alimenta o array `protocol` do
 * evento `prescription_completed`.
 *
 * As coleções são strings JSON porque o Amplitude não suporta arrays aninhados dentro de
 * arrays.
 *
 * @param model Modelo aplicado.
 * @param modelDetails Detalhes do modelo, já carregados.
 * @param isEdited `true` quando o médico removeu itens do modelo ao aplicá-lo.
 * @param formulaNames Nomes das fórmulas manipuladas — `/protocols` só traz o id.
 * @returns O registro serializado do protocolo.
 */
function buildPrescriptionCompletedProtocol(
  model: Model,
  modelDetails: ModelDetails,
  isEdited: boolean,
  formulaNames?: Map<number, string>,
): PrescriptionCompletedProtocol {
  const examItemsFlat = (modelDetails.exams ?? []).flatMap((group) => group.items ?? []);
  return {
    id: model.id,
    name: modelDetails.name,
    is_sponsored: model.sponsored || false,
    is_institutional: model.origin === 'institutional',
    created_at: modelDetails.createdAt ?? '',
    updated_at: modelDetails.updatedAt ?? '',
    is_edited: isEdited,
    medications: JSON.stringify((modelDetails.medications ?? []).map((medication) => ({
      id: medication.id,
      name: medication.brandName ?? medication.activeIngredient ?? '',
    }))),
    medical_test: JSON.stringify(examItemsFlat.map((item) => ({
      id: item.id,
      name: item.description ?? '',
    }))),
    doctors_note: JSON.stringify((modelDetails.certificates ?? []).map((note) => ({
      id: note.id,
      type: note.type ?? '',
    }))),
    referral: JSON.stringify((modelDetails.referrals ?? []).map((referral) => ({
      id: referral.id,
      specialty: referral.specialty ?? '',
    }))),
    medical_report: JSON.stringify((modelDetails.reports ?? []).map((report) => ({
      id: report.id,
      type: report.documentType ?? '',
    }))),
    compounded_medications: JSON.stringify((modelDetails.compoundedFormulas ?? []).map((f) => ({
      id: f.id,
      name: formulaNames?.get(f.formulaId) ?? '',
    }))),
  };
}

/**
 * Listagem de primeira página **viva** ainda em voo, com a chave dos parâmetros que a originou.
 *
 * O carregamento inicial da tela de modelos tem mais de um dono — o bootstrap do
 * `PrescriptionProvider` e os efeitos de `PrescriptionModels`/`SelectModelDrawer` — e os
 * guards deles leem estado que só era escrito depois do `await` da especialidade. Resultado:
 * dois GET /protocols idênticos antes da lista aparecer. Aqui a chamada equivalente ainda em
 * voo é reaproveitada em vez de virar uma nova requisição.
 *
 * É um slot, e não um mapa por `key`, porque `modelsRequestId` é global: disparar qualquer
 * listagem torna obsoleta toda listagem anterior ainda pendente, que ao voltar cai no `isStale`
 * e não aplica nada. Guardar as obsoletas faria uma chamada nova se juntar a uma resposta que
 * já será descartada — a tela ficaria esperando uma atualização que nunca chega. Por isso a
 * requisição mais recente toma o slot, e o `.finally` só o libera se ele ainda for o dela
 * (comparação por identidade): a que perdeu o lugar não pode apagar o registro de quem a
 * substituiu, e o slot nunca é liberado enquanto a requisição que vale está pendente.
 */
let inFlightModelsFetch: { key: string; promise: Promise<void> } | null = null;

/**
 * Descarta a listagem em voo.
 *
 * Existe para teste: `inFlightModelsFetch` é estado de módulo e não é zerado pelo
 * `useModelStore.setState`, então um caso que deixa promise pendente contamina o próximo. Não
 * chame em código de produção — quem precisa furar a deduplicação usa `options.force`, que
 * preserva a ordenação por `modelsRequestId`.
 *
 * @returns Nada; a próxima chamada a `fetchModels` volta a disparar uma requisição.
 *
 * @internal
 */
export function resetModelsFetchDedupe(): void {
  inFlightModelsFetch = null;
}

/**
 * Extrai os patrocinados de uma página da listagem, preservando os já conhecidos.
 *
 * Chamado na página 1 (`reset`) e em cada página seguinte do scroll infinito. Sem acumular, um
 * patrocinado que só aparece na página 3 nunca entraria em `sponsoredModels` — e qualquer
 * consumidor dela (merge do `filteredModels`, tracking) veria uma lista incompleta.
 */
function mergeSponsored(previous: Model[], page: Model[], reset: boolean): Model[] {
  const fromPage = page.filter((model) => model.sponsored);
  if (reset) return fromPage;
  if (fromPage.length === 0) return previous;

  const known = new Set(previous.map((model) => model.id));
  return [...previous, ...fromPage.filter((model) => !known.has(model.id))];
}

/**
 * Tracking de inserção de modelo patrocinado na prescrição.
 *
 * Dispara `backoffice_prescription_models_view_addmodel` (adição confirmada) e
 * `Modelo_inserido_item_patrocinado` (todos os medicamentos do modelo). Só roda para
 * `model.sponsored` — o `highlighted`/`destacado` não-patrocinado foi descontinuado no
 * backend, então não existe mais um `tipoModelo: 1` para logar.
 *
 * Fire-and-forget: tracking não deve bloquear nem quebrar a aplicação do modelo.
 */
function trackSponsoredModelInsertion(
  model: Model,
  modelDetails: ModelDetails,
  prescription: NonNullable<ReturnType<typeof usePrescriptionStore.getState>['prescription']>,
  entryPoint?: ModelEntryPoint
): void {
  if (!model.sponsored) return;

  const prescriberConfiguration = usePrescriptionStore.getState().prescriberConfiguration;
  const prescriber = usePrescriptionStore.getState().prescriber();
  const medicationIds = (modelDetails.medications ?? [])
    .map((medication) => medication.medicationId)
    .filter((id): id is string => Boolean(id));

  TrackerEventService.track(prescription.idPrescricao, 'backoffice_prescription_models_view_addmodel', {
    idMedico: prescriber?.idPrescritor,
    idPrescricao: prescription.idPrescricao,
    idParceiro: prescriberConfiguration?.idParceiro,
    codigoValidacao: prescription.CodigoValidacao,
    tipoModelo: 2,
    idPrescricaoModelo: model.id,
    idMedicamento: medicationIds,
    isMobile: isMobileWeb(),
    entryPoint,
  }).catch((error) => {
    logger.error('trackSponsoredModelInsertion: backoffice_prescription_models_view_addmodel failed', error);
  });

  TrackerEventService.track(prescription.idPrescricao, 'Modelo_inserido_item_patrocinado', {
    idPrescricao: prescription.idPrescricao,
    idPrescricaoModelo: model.id,
    idMedico: prescriber?.idPrescritor,
    idMedicamento: medicationIds,
    entryPoint,
  }).catch((error) => {
    logger.error('trackSponsoredModelInsertion: Modelo_inserido_item_patrocinado failed', error);
  });
}

export const useModelStore = create<ModelStore>()((set, get) => ({
  models: [],
  /**
   * Carrega a primeira página de modelos, opcionalmente filtrada por nome.
   *
   * Sem `partnerId` não dá pra chamar o protocols-service (o backend exige). Nesse caso
   * abortamos sem marcar erro e sem limpar a lista: é um estado transitório enquanto o
   * `prescriberConfiguration` carrega, e um estado de erro aqui deixaria a tela travada
   * num "Tentar novamente" que cairia sempre no mesmo early return.
   *
   * Uma requisição equivalente ainda em voo é reaproveitada em vez de gerar um segundo
   * GET /protocols — use `options.force` depois de mutações, quando a resposta pendente já
   * nasceu desatualizada.
   *
   * @param prescriberId Prescritor dono dos modelos.
   * @param prescriptionId Prescrição em contexto, exigida pelo endpoint de listagem.
   * @param age Faixa etária do paciente, usada pelo backend para priorizar modelos.
   * @param name Termo de busca por nome; `undefined` lista sem filtro.
   * @param options `force` ignora a deduplicação da requisição em voo.
   */
  async fetchModels(
    prescriberId: number,
    prescriptionId: number,
    age?: string,
    name?: string,
    options?: FetchModelsOptions,
  ): Promise<void> {
    const { prescriberConfiguration, rqes } = usePrescriptionStore.getState();
    const enablePharmaIndustryModel = LodashGet(prescriberConfiguration, 'Features.enablePharmaIndustryModel', false);
    const enableSponsoredModel = LodashGet(prescriberConfiguration, 'Features.enableSponsoredModel', false);
    const partnerId = prescriberConfiguration?.idParceiro;

    if (!partnerId) {
      logger.warn('fetchModels: partnerId não disponível (prescriberConfiguration ainda não carregada). Skipping.');
      return;
    }

    /**
     * Tudo que muda a resposta entra na chave — inclusive os RQEs.
     *
     * A especialidade é resolvida a partir de `rqes` depois do primeiro `await`, então duas
     * chamadas com `rqes` diferentes produzem listas diferentes. Sem os RQEs aqui, a segunda
     * seria deduplicada na primeira e herdaria a especialidade errada — hoje isso não acontece
     * porque o `setRqes` do `PrescriptionProvider` roda antes do `fetchModels` do bootstrap,
     * mas esse acoplamento é invisível e não deve ser uma pré-condição silenciosa.
     */
    const requestKey = JSON.stringify([
      prescriberId,
      prescriptionId,
      partnerId,
      enablePharmaIndustryModel,
      age ?? null,
      name ?? null,
      rqes?.map((rqe) => rqe.idRqe ?? null) ?? null,
    ]);

    if (!options?.force && inFlightModelsFetch?.key === requestKey) return inFlightModelsFetch.promise;

    /**
     * O contador sobe antes de qualquer `await`: é ele que ordena as respostas (`isStale`) e
     * que a UI lê como "já existe uma busca em andamento". Incrementando só depois do await
     * da especialidade, duas buscas simultâneas podiam receber ids fora da ordem de disparo e
     * a resposta antiga sobrescrever a nova.
     */
    const requestId = get().modelsRequestId + 1;
    set((prev) => ({ ...prev, modelsRequestId: requestId }));
    const isStale = () => get().modelsRequestId !== requestId;

    const run = async (): Promise<void> => {
      const speciality = await resolveDoctorSpeciality(rqes, prescriberId);

      if (isStale()) return;

      let modelList: Awaited<ReturnType<typeof ModelService.fetchModels>>;
      try {
        modelList = await ModelService.fetchModels(
          prescriberId,
          prescriptionId,
          partnerId,
          enablePharmaIndustryModel,
          1,
          MODELS_PAGE_SIZE,
          age,
          name,
          speciality,
        );
      } catch (e) {
        if (isStale()) return;
        logger.error('fetchModels: failed loading models', e);
        set((prev) => ({ ...prev, modelsError: true, loadingMoreModels: false }));
        return;
      }

      if (isStale()) return;

      const data = modelList.data || [];
      /**
       * Com `includeSponsoredModels` a própria listagem já traz os patrocinados — não faz
       * sentido pedi-los de novo num segundo GET /protocols. A pill "Sugestão" agrupa por
       * `sponsored`, e `loadMoreModels` acrescenta os das páginas seguintes a esta lista — ver
       * `mergeSponsored`.
       */
      const listCarriesSponsored = enableSponsoredModel && enablePharmaIndustryModel;

      set((prev) => {
        return {
          ...prev,
          models: data,
          modelsTotal: modelList.total ?? data.length,
          modelsPage: 1,
          modelsAge: age,
          modelsName: name,
          modelsSpeciality: speciality,
          modelsError: false,
          isInstitutionalModelAdmin: modelList.administradorModeloInstitucional ?? false,
          loadingMoreModels: false,
          ...(listCarriesSponsored
            ? { sponsoredModels: mergeSponsored(prev.sponsoredModels, data, true), sponsoredModelsFromListing: true }
            : {}),
        };
      });
    };

    let entry: { key: string; promise: Promise<void> } | null = null;
    const promise = run().finally(() => {
      if (inFlightModelsFetch === entry) inFlightModelsFetch = null;
    });
    entry = { key: requestKey, promise };
    inFlightModelsFetch = entry;

    return promise;
  },
  modelsTotal: 0,
  modelsPage: 1,
  modelsError: false,
  modelsRequestId: 0,
  isInstitutionalModelAdmin: false,
  loadingMoreModels: false,
  hasMoreModels: () => get().models.length < get().modelsTotal,
  /**
   * Anexa a próxima página à lista atual (scroll infinito).
   *
   * Assim como o `fetchModels`, a resposta só é aplicada se ainda for a mais recente: uma
   * busca disparada enquanto a página está em voo troca a lista inteira, e anexar o
   * resultado antigo por cima corromperia `modelsPage`/`modelsTotal` — a página seguinte
   * do novo termo seria pulada.
   *
   * @param prescriberId Prescritor dono dos modelos.
   * @param prescriptionId Prescrição em contexto, exigida pelo endpoint de listagem.
   */
  async loadMoreModels(prescriberId: number, prescriptionId: number): Promise<void> {
    if (get().loadingMoreModels || !get().hasMoreModels()) return;

    /**
     * Com a primeira página ainda em voo, a página 2 sairia antes de a lista aparecer em tela
     * (o sentinel do scroll infinito nasce visível quando a grade é curta).
     *
     * Contrato com quem chama: este early return é silencioso, então o re-disparo é
     * responsabilidade do efeito que observa o sentinel — que precisa ser recriado a cada
     * transição da listagem (`modelsRequestId`/`modelsError`), e não só quando a lista muda.
     * Uma primeira página que falha não altera `models`, e sem essas dependências a paginação
     * ficaria parada até o usuário rolar o sentinel para fora e de volta.
     */
    if (inFlightModelsFetch) return;

    const prescriberConfiguration = usePrescriptionStore.getState().prescriberConfiguration;
    const enablePharmaIndustryModel = LodashGet(prescriberConfiguration, 'Features.enablePharmaIndustryModel', false);
    const partnerId = prescriberConfiguration?.idParceiro;
    if (!partnerId) return;

    const nextPage = get().modelsPage + 1;
    const requestId = get().modelsRequestId;
    const isStale = () => get().modelsRequestId !== requestId;
    set((prev) => ({ ...prev, loadingMoreModels: true }));

    try {
      const modelList = await ModelService.fetchModels(
        prescriberId,
        prescriptionId,
        partnerId,
        enablePharmaIndustryModel,
        nextPage,
        MODELS_PAGE_SIZE,
        get().modelsAge,
        get().modelsName,
        get().modelsSpeciality,
      );

      if (isStale()) {
        set((prev) => ({ ...prev, loadingMoreModels: false }));
        return;
      }

      const enableSponsoredModel = LodashGet(prescriberConfiguration, 'Features.enableSponsoredModel', false);
      const listCarriesSponsored = enableSponsoredModel && enablePharmaIndustryModel;

      set((prev) => {
        const existingIds = new Set(prev.models.map((m) => m.id));
        const incoming = (modelList.data ?? []).filter((m) => !existingIds.has(m.id));
        return {
          ...prev,
          models: [...prev.models, ...incoming],
          modelsPage: nextPage,
          modelsTotal: modelList.total ?? prev.modelsTotal,
          loadingMoreModels: false,
          // Patrocinado que só aparece a partir da página 2 também precisa entrar na lista.
          ...(listCarriesSponsored
            ? { sponsoredModels: mergeSponsored(prev.sponsoredModels, incoming, false) }
            : {}),
        };
      });
    } catch (e) {
      if (!isStale()) logger.error('loadMoreModels: failed loading next page', { page: nextPage, error: e });
      set((prev) => ({ ...prev, loadingMoreModels: false }));
    }
  },
  sponsoredModels: [],
  sponsoredModelsFromListing: false,
  /**
   * Garante que `sponsoredModels` esteja preenchida, pela listagem ou pelo endpoint dedicado.
   *
   * Com `enablePharmaIndustryModel` ligada a listagem já sai com `includeSponsoredModels=true`
   * e os patrocinados vêm dela — aqui a chamada é dispensada, contanto que a listagem tenha
   * mesmo entregado (ver `sponsoredModelsFromListing`). Com a flag desligada, ou quando a
   * listagem falhou, o endpoint dedicado é a única fonte da seção "Sugestão".
   *
   * Sem `enableSponsoredModel` a lista é zerada em vez de mantida: a flag pode ter sido
   * desligada em runtime e o prescritor não pode continuar vendo patrocinado na tela.
   *
   * @param prescriberId Prescritor dono dos modelos.
   * @param prescriptionId Prescrição em contexto, exigida pelo endpoint.
   * @param age Faixa etária do paciente, usada pelo backend para priorizar modelos.
   * @returns Nada; o resultado é publicado em `sponsoredModels`.
   */
  async fetchSponsoredModels(prescriberId: number, prescriptionId: number, age?: string): Promise<void> {
    const prescriberConfiguration = usePrescriptionStore.getState().prescriberConfiguration;

    const enableSponsoredModel = LodashGet(prescriberConfiguration, 'Features.enableSponsoredModel', false);
    const enablePharmaIndustryModel = LodashGet(prescriberConfiguration, 'Features.enablePharmaIndustryModel', false);
    const partnerId = prescriberConfiguration?.idParceiro;

    // Feature flag desligada: zera a lista (caso a flag tenha mudado em runtime) e aborta.
    // Não bate no endpoint nem reusa estado anterior.
    if (!enableSponsoredModel) {
      set((prev) => ({ ...prev, sponsoredModels: [], sponsoredModelsFromListing: false }));
      return;
    }

    /**
     * `enablePharmaIndustryModel` faz a listagem sair com `includeSponsoredModels=true`, então
     * os patrocinados já vieram no `fetchModels` — este endpoint é o mesmo GET /protocols e
     * repetiria a consulta. Com a flag desligada a listagem não os traz e a busca dedicada
     * continua sendo necessária.
     *
     * O atalho depende de entrega, não de tentativa: a listagem que ainda não rodou, que
     * abortou sem `idParceiro` (transitório no boot) ou que falhou no GET não populou
     * `sponsoredModels` — e pular aqui nesses casos deixaria a seção "Sugestão" vazia pelo
     * resto da sessão, sem ninguém para preenchê-la. Esperar a listagem em voo antes de decidir
     * evita o outro extremo: concluir cedo demais que ela não entregou e repetir a consulta.
     */
    if (enablePharmaIndustryModel) {
      if (inFlightModelsFetch) await inFlightModelsFetch.promise.catch(() => undefined);
      if (get().sponsoredModelsFromListing) return;
    }

    if (!partnerId) {
      logger.warn('fetchSponsoredModels: partnerId não disponível. Skipping.');
      return;
    }

    const modelList = await ModelService.fetchSponsoredModels(prescriberId, prescriptionId, partnerId, age);

    set((prev) => {
      return {
        ...prev,
        sponsoredModels: modelList.data,
      };
    });
  },
  filter: FILTER_TYPES.ALL,
  setFilter: (filter: FILTER_TYPES) => set((prev) => ({ ...prev, filter })),
  searchFilter: '',
  setSearchFilter: (value: string) => set((prev) => ({ ...prev, searchFilter: value })),

  groupedModels: () => computeGroupedModels(get().filteredModels()),

  filteredModels: () => {
    const data = get().models;
    // Temporário: merge de sponsoredModels (endpoint separado) na lista principal. Remover
    // quando o fetchModels já retornar os patrocinados/sugestão junto.
    const sponsored = get().sponsoredModels;
    const searchFilter = get().searchFilter;

    const existingIds = new Set(data.map((model) => model.id));
    const merged = [...data, ...sponsored.filter((model) => !existingIds.has(model.id))];

    if (isEmpty(merged)) return [];

    // `includes` em vez de `match` porque `match` interpreta o input como regex — nomes com
    // caracteres especiais (`(`, `)`, `[`, `.`) quebrariam a busca ou dariam resultado errado.
    const needle = searchFilter.trim().toLowerCase();
    if (!needle) return merged;
    return merged.filter((model) => model.name.toLowerCase().includes(needle));
  },

  modelsLength: () => {
    const groupedModels = get().groupedModels();

    return {
      all: groupedModels.all.length,
      institutional: groupedModels.institutional.length,
      personal: groupedModels.personal.length,
      highlighted: groupedModels.highlighted.length,
    };
  },
  isCreatingModel: false,
  setIsCreatingModel: (value: boolean) => set((prev) => ({ ...prev, isCreatingModel: value })),

  sponsoredModelDetails: [],
  async fetchSponsoredModelDetails(): Promise<void> {
    const prescription = usePrescriptionStore.getState().prescription;

    if (!prescription) return;

    try {
      const sponsoredDetails = await Promise.all(
        get().sponsoredModels.map(async (model) => {
          const details = await ModelService.fetchModelDetails(
            model.id,
            prescription.Prescritor?.idPrescritor ?? 0,
            usePrescriptionStore.getState().prescriberConfiguration?.idParceiro ?? 0,
            prescription.idPrescricao,
          );
          return { ...details, id: model.id };
        })
      );
      set((prev) => {
        return {
          ...prev,
          sponsoredModelDetails: sponsoredDetails,
        };
      });
    } catch (error) {
      logger.error('fetchSponsoredModelDetails: failed to fetch details', error);
    }
  },

  feedback: null,
  setFeedback: (feedback: ModelFeedback) => set((prev) => ({ ...prev, feedback })),

  lastAppliedItems: null,
  isUndoingApplication: false,
  async applyModelToPrescription(
    model: Model,
    selection?: ApplySelection,
    details?: ModelDetails,
    entryPoint?: ModelEntryPoint
  ): Promise<void> {
    const prescriptionStoreState = usePrescriptionStore.getState();
    const prescription = prescriptionStoreState.prescription;

    if (!prescription) return;

    try {
      const modelDetails =
        details ??
        (await ModelService.fetchModelDetails(
          model.id,
          prescription.Prescritor?.idPrescritor ?? 0,
          prescriptionStoreState.prescriberConfiguration?.idParceiro ?? 0,
          prescription.idPrescricao,
        ));

      const beforeSnapshot = collectPrescriptionItemIds(prescription);
      const previousObservation = prescription.Comentario ?? null;

      const formulaNames = await resolveFormulaNames(modelDetails);
      const prescriberConselho = usePrescriptionStore.getState().prescriber()?.Conselho;

      prescriptionStoreState.setItemsAddedFromModel(true);

      const { unresolvedCidCodes, unlinkedExamCount, missingLateralityCount, failedItems } =
        await addProtocolItemsToPrescription(modelDetails, selection, { formulaNames, prescriberConselho });

      const afterPrescription = usePrescriptionStore.getState().prescription;
      if (!afterPrescription) {
        prescriptionStoreState.setItemsAddedFromModel(false);
        set((prev) => ({ ...prev, feedback: { type: 'error', modelName: model.name } }));
        return;
      }

      const afterSnapshot = collectPrescriptionItemIds(afterPrescription);
      const addedItems: AppliedItemsSnapshot = {
        prescriptionId: prescription.idPrescricao,
        medicineIds: diffArrays(afterSnapshot.medicineIds, beforeSnapshot.medicineIds),
        manualMedicineIds: diffArrays(afterSnapshot.manualMedicineIds, beforeSnapshot.manualMedicineIds),
        examIds: diffArrays(afterSnapshot.examIds, beforeSnapshot.examIds),
        certificateIds: diffArrays(afterSnapshot.certificateIds, beforeSnapshot.certificateIds),
        referralIds: diffArrays(afterSnapshot.referralIds, beforeSnapshot.referralIds),
        reportIds: diffArrays(afterSnapshot.reportIds, beforeSnapshot.reportIds),
        formulaIds: diffArrays(afterSnapshot.formulaIds, beforeSnapshot.formulaIds),
        previousObservation,
      };

      try {
        const isEdited = selection !== undefined;

        saveProtocolData(
          prescription.idPrescricao,
          buildPrescriptionCompletedProtocol(model, modelDetails, isEdited, formulaNames),
        );

        const prescriberConfiguration = usePrescriptionStore.getState().prescriberConfiguration;
        amplitudeModule.protocolSelected({
          screen_name: entryPoint ?? 'seleção de modelo',
          flow: 'seleção de modelo',
          partner_id: prescriberConfiguration?.idParceiro ?? 0,
          partner_name: prescriberConfiguration?.Nome ?? '',
          establishment_name: prescription.Estabelecimento?.Nome ?? '',
          protocol: [buildProtocolEventProtocol(model, modelDetails, isEdited, prescriberConselho, formulaNames)],
        });

        Tracker.trackEvent({
          event: 'Model Selected',
          parameters: {
            model: modelDetails.name,
            entryPoint,
          },
        });

        trackSponsoredModelInsertion(model, modelDetails, prescription, entryPoint);
      } catch {
      }

      set((prev) => ({
        ...prev,
        lastAppliedItems: addedItems,
        feedback: {
          type: 'success',
          modelName: model.name,
          unresolvedCidCount: unresolvedCidCodes.length || undefined,
          unlinkedExamCount: unlinkedExamCount || undefined,
          missingLateralityCount: missingLateralityCount || undefined,
          failedItems: failedItems.length ? failedItems : undefined,
        },
      }));
    } catch (error) {
      logger.error('Error applying model:', error);
      prescriptionStoreState.setItemsAddedFromModel(false);
      set((prev) => ({
        ...prev,
        feedback: { type: 'error', modelName: model.name },
      }));
    }
  },

  async undoLastApplication(): Promise<void> {
    const snapshot = get().lastAppliedItems;
    const prescriptionStoreState = usePrescriptionStore.getState();
    const prescription = prescriptionStoreState.prescription;

    if (!snapshot || !prescription) return;
    if (snapshot.prescriptionId !== prescription.idPrescricao) {
      set((prev) => ({ ...prev, lastAppliedItems: null, feedback: null }));
      return;
    }

    set((prev) => ({ ...prev, isUndoingApplication: true }));

    try {
      for (const id of snapshot.medicineIds) {
        const medicine = prescription.Itens.find((m) => m.idPrescricaoMedicamento === id);
        if (!medicine) continue;
        try {
          await prescriptionStoreState.removeMedicineFromPrescription(medicine);
        } catch (e) {
          logger.error('Undo: failed removing medicine', e);
        }
      }

      for (const id of snapshot.manualMedicineIds) {
        const manual = prescription.ItensManuais.find((m) => m.idPrescricaoMedicamentoManual === id);
        if (!manual) continue;
        try {
          await prescriptionStoreState.removePrescriptionManualMedicine(manual);
        } catch (e) {
          logger.error('Undo: failed removing manual medicine', e);
        }
      }

      for (const id of snapshot.examIds) {
        const exam = prescription.Exames.find((e) => e.idPrescricaoExame === id);
        if (!exam) continue;
        try {
          await prescriptionStoreState.removeExamFromPrescription(exam);
        } catch (e) {
          logger.error('Undo: failed removing exam', e);
        }
      }

      for (const id of snapshot.certificateIds) {
        try {
          await prescriptionStoreState.removeMedicalCertificateFromPrescription(id);
        } catch (e) {
          logger.error('Undo: failed removing certificate', e);
        }
      }

      for (const id of snapshot.referralIds) {
        try {
          await prescriptionStoreState.removeMedicalReferralFromPrescription(id);
        } catch (e) {
          logger.error('Undo: failed removing referral', e);
        }
      }

      for (const id of snapshot.reportIds) {
        try {
          await prescriptionStoreState.removeMedicalReportFromPrescription(id);
        } catch (e) {
          logger.error('Undo: failed removing report', e);
        }
      }

      for (const id of snapshot.formulaIds) {
        try {
          await FormulaService.deleteFormulaFromPrescription(prescription.idPrescricao, id);
        } catch (e) {
          logger.error('Undo: failed removing formula', e);
        }
      }

      try {
        const refreshed = await PrescriptionService.fetchPrescriptionById(prescription.idPrescricao);
        usePrescriptionStore.setState((prev) => ({ ...prev, prescription: refreshed }));
      } catch (e) {
        logger.error('Undo: failed refreshing prescription', e);
      }

      // `silent`: o toast genérico da observação não cabe aqui — quem falhou foi a restauração,
      // e o médico precisa saber disso com o texto certo.
      try {
        await prescriptionStoreState.onUpdatePrescriptionObservation(snapshot.previousObservation, true);
      } catch (e) {
        logger.error('Undo: failed restoring observation', e);
        toast.error('Não foi possível restaurar a observação anterior, revise antes de emitir.');
      }

      prescriptionStoreState.setItemsAddedFromModel(false);
      set((prev) => ({ ...prev, lastAppliedItems: null, feedback: null }));
    } catch (error) {
      logger.error('Error undoing model application:', error);
    } finally {
      set((prev) => ({ ...prev, isUndoingApplication: false }));
    }
  },

}));

/**
 * Termo de busca atualmente aplicado à listagem de modelos.
 *
 * Recarregamentos disparados por criação, edição ou exclusão precisam repetir o filtro em
 * vigor. Sem isso `modelsName` fica dessincronizado de `searchFilter`, e a paginação passa
 * a percorrer a lista inteira em vez do resultado da busca — o médico só encontra os
 * modelos que combinam com o termo depois de rolar várias páginas irrelevantes.
 *
 * @returns O termo aplicado, ou `undefined` quando a busca está vazia.
 */
export function activeModelSearchName(): string | undefined {
  return useModelStore.getState().searchFilter.trim() || undefined;
}

function collectPrescriptionItemIds(prescription: NonNullable<ReturnType<typeof usePrescriptionStore.getState>['prescription']>) {
  return {
    medicineIds: (prescription.Itens ?? []).map((m: Medicine) => m.idPrescricaoMedicamento),
    manualMedicineIds: (prescription.ItensManuais ?? [])
      .map((m: ManualMedicine) => m.idPrescricaoMedicamentoManual)
      .filter((id): id is number => id !== null && id !== undefined),
    examIds: (prescription.Exames ?? [])
      .map((e: Exam) => e.idPrescricaoExame)
      .filter((id): id is number => typeof id === 'number'),
    certificateIds: (prescription.Atestados ?? [])
      .map((c: MedicalCertificate) => c.idPrescricaoAtestado)
      .filter((id): id is number => typeof id === 'number'),
    referralIds: (prescription.Encaminhamentos ?? [])
      .map((r: MedicalReferral) => r.idEncaminhamentoPrescricao)
      .filter((id): id is number => typeof id === 'number'),
    reportIds: (prescription.Relatorios ?? [])
      .map((r: MedicalReport) => r.idPrescricaoLaudoMedico)
      .filter((id): id is number => typeof id === 'number'),
    formulaIds: (prescription.Formulas ?? [])
      .map((f: Formula) => f.id)
      .filter((id): id is number => typeof id === 'number'),
  };
}

function diffArrays(after: number[], before: number[]): number[] {
  const beforeSet = new Set(before);
  return after.filter((id) => !beforeSet.has(id));
}

/**
 * Agrupa os modelos nos buckets usados pela UI.
 *
 * - `highlighted`: modelos com `sponsored: true` (alimenta a pill "Sugestão")
 * - `institutional`: `origin === 'institutional'`, exceto patrocinados
 * - `personal`: o resto (modelos do próprio médico)
 * - `all`: ordem visual final (highlighted → institutional → personal)
 *
 * Cada bucket é ordenado por `name` com comparação locale-aware pt-BR. Função pura — pode
 * ser usada em `useMemo` no componente para evitar que o seletor zustand crie um objeto novo
 * a cada render.
 *
 * @param models Modelos já filtrados.
 * @returns Os buckets e a lista final ordenada.
 */
export function computeGroupedModels(models: Model[]): GroupedModels {
  if (isEmpty(models)) {
    return { institutional: [], personal: [], highlighted: [], all: [] };
  }

  const sortByName = (a: Model, b: Model) =>
    (a.name ?? '').localeCompare(b.name ?? '', 'pt-BR', { sensitivity: 'base' });

  const buckets = models.reduce(
    (acc: { institutional: Model[]; personal: Model[]; highlighted: Model[] }, cur: Model) => {
      // O campo `highlighted` foi descontinuado no backend; hoje o único critério da pill
      // "Sugestão" é `sponsored: true`.
      if (cur.sponsored) {
        acc.highlighted.push(cur);
      } else if (cur.origin === 'institutional') {
        acc.institutional.push(cur);
      } else {
        acc.personal.push(cur);
      }
      return acc;
    },
    { institutional: [], personal: [], highlighted: [] }
  );

  buckets.highlighted.sort(sortByName);
  buckets.institutional.sort(sortByName);
  buckets.personal.sort(sortByName);

  return {
    ...buckets,
    all: [...buckets.highlighted, ...buckets.institutional, ...buckets.personal],
  };
}

/**
 * Espelho puro de `useModelStore.filteredModels`.
 *
 * Mescla `models` + `sponsoredModels` (dedupe por id) e filtra por nome. Usar em `useMemo` no
 * componente, em vez do seletor `useModelStore((s) => s.filteredModels())`, evita re-renders.
 *
 * @param models Modelos da listagem principal.
 * @param sponsoredModels Modelos patrocinados, vindos de endpoint separado.
 * @param searchFilter Termo digitado pelo médico.
 * @returns Os modelos mesclados que combinam com o termo.
 */
export function computeFilteredModels(
  models: Model[],
  sponsoredModels: Model[],
  searchFilter: string,
): Model[] {
  const existingIds = new Set(models.map((m) => m.id));
  const merged = [...models, ...sponsoredModels.filter((m) => !existingIds.has(m.id))];
  if (isEmpty(merged)) return [];
  const needle = searchFilter.trim().toLowerCase();
  if (!needle) return merged;
  return merged.filter((m) => m.name.toLowerCase().includes(needle));
}

/**
 * Resolve no catálogo os CIDs referenciados pelo modelo.
 *
 * O contrato guarda só o código; a prescrição precisa do CID estruturado (id + descrição).
 *
 * @param details Detalhes do modelo.
 * @returns Os CIDs resolvidos, indexados por código; os não encontrados ficam de fora.
 */
async function resolveProtocolCids(details: ProtocolDetail): Promise<Map<string, Cid>> {
  const resolved = new Map<string, Cid>();
  const codes = collectProtocolCidCodes(details);
  if (codes.length === 0) return resolved;

  await Promise.all(
    codes.map(async (code) => {
      try {
        const results = await PatientService.getIcd(code);
        const match = (results ?? []).find(
          (icd) => icd.subcategory?.toUpperCase() === code.toUpperCase()
        );
        if (!match) {
          logger.warn('resolveProtocolCids: CID do modelo não encontrado na busca', { code });
          return;
        }
        resolved.set(code, {
          Codigo: match.subcategory,
          idCID10: match.id,
          Descricao: match.description,
        });
      } catch (e) {
        logger.error('resolveProtocolCids: failed resolving CID', { code, error: e });
      }
    })
  );

  return resolved;
}

/**
 * Resolve no catálogo os exames referenciados pelo modelo, pelo código TUSS.
 *
 * @param details Detalhes do modelo.
 * @param prescriptionId Prescrição em contexto, exigida pela busca de exames.
 * @param doctorId Prescritor em contexto, exigido pela busca de exames.
 * @returns Os exames resolvidos, indexados por código TUSS; os não encontrados ficam de fora.
 */
async function resolveProtocolExams(
  details: ProtocolDetail,
  prescriptionId: number,
  doctorId: number
): Promise<Map<string, ExamSearch>> {
  const resolved = new Map<string, ExamSearch>();
  const codes = Array.from(
    new Set(
      (details.exams ?? [])
        .flatMap((group) => (group.items ?? []).map((item) => item.tussCode))
        .filter((code): code is string => Boolean(code))
    )
  );
  if (codes.length === 0) return resolved;

  await Promise.all(
    codes.map(async (code) => {
      try {
        const { exams } = await ExamService.searchExam({ search: code, prescriptionId, doctorId });
        const match = (exams ?? []).find((exam) => exam.CodigoTUSS === code);
        if (!match) {
          logger.warn('resolveProtocolExams: exame do modelo não encontrado no catálogo', { code });
          return;
        }
        resolved.set(code, match);
      } catch (e) {
        logger.error('resolveProtocolExams: failed resolving exam', { code, error: e });
      }
    })
  );

  return resolved;
}

/**
 * Resolve os nomes das fórmulas manipuladas do modelo.
 *
 * O payload de `/protocols` traz só o `formulaId`; o nome vem da biblioteca do médico e é
 * usado tanto nos eventos de analytics quanto nos rótulos do toast de aplicação parcial.
 *
 * @param details Detalhes do modelo.
 * @returns Os nomes encontrados, indexados por `formulaId`.
 */
async function resolveFormulaNames(details: ProtocolDetail): Promise<Map<number, string>> {
  const names = new Map<number, string>();
  await Promise.all(
    (details.compoundedFormulas ?? []).map(async (formula) => {
      try {
        const lib = await FormulaService.getDoctorFormulaById(details.doctorId, formula.formulaId);
        if (lib?.formulaName) names.set(formula.formulaId, lib.formulaName);
      } catch {
      }
    })
  );
  return names;
}

/**
 * Enriquece um medicamento vindo de modelo com os dados de laboratório de genéricos.
 *
 * O tipo do medicamento vem do catálogo, já que o contrato de protocolos não o
 * guarda. A seleção de laboratório segue esta precedência:
 *
 * 1. laboratório gravado no modelo, quando ainda consta no catálogo;
 * 2. laboratório patrocinado, quando o modelo não gravou nenhum;
 * 3. sem preferência, quando não há gravado nem patrocinado.
 *
 * @param medicine Medicamento convertido a partir do modelo.
 * @param store Estado atual da prescrição, usado para prescritor e parceiro.
 * @returns O medicamento com os campos de laboratório, ou inalterado quando não se aplica.
 */
async function withModelGenericLabFields(
  medicine: Medicine,
  store: ReturnType<typeof usePrescriptionStore.getState>
): Promise<Medicine> {
  const enableGenericLabSelection = LodashGet(
    store.prescriberConfiguration,
    'Features.enableGenericLabSelection',
    true
  );

  if (!enableGenericLabSelection || !store.prescription) return medicine;

  try {
    const { laboratorios, idLaboratorioPatrocinado, isGeneric } = await resolveGenericLabInfo({
      medicineId: medicine.IdMedicamento,
      prescriptionId: store.prescription.idPrescricao,
      doctorId: store.prescriber()?.idPrescritor ?? 0,
      partnerId: store.prescriberConfiguration?.idParceiro,
    });

    if (isGeneric === null) {
      logger.info('withModelGenericLabFields: catálogo não informou o tipo do medicamento', {
        idMedicamento: medicine.IdMedicamento,
      });
    }

    if (!isGeneric || !laboratorios.length) return medicine;

    const savedLaboratoryId = medicine.LaboratorioPreferencia || null;
    const isSavedStillAvailable =
      savedLaboratoryId !== null && laboratorios.some((lab) => lab.id === savedLaboratoryId);

    return {
      ...medicine,
      LaboratoriosDisponiveis: laboratorios,
      idLaboratorioSelecionado: isSavedStillAvailable ? savedLaboratoryId : idLaboratorioPatrocinado,
      idLaboratorioPatrocinado,
    };
  } catch (error) {
    logger.error('withModelGenericLabFields: falha ao resolver laboratório de genéricos', error);
    return medicine;
  }
}

/**
 * Aplica os itens de um `ProtocolDetail` à prescrição atual.
 *
 * Sem `selection`, todos os itens são adicionados; com ela, apenas os índices presentes em
 * cada Set. Erros por item são logados e devolvidos como rótulo em `failedItems`, sem
 * interromper o fluxo — assim a maior parte do modelo entra mesmo que um item falhe.
 *
 * As seis categorias rodam em paralelo entre si; dentro de cada uma a inserção é sequencial,
 * porque a ordem dos itens na prescrição vem da ordem em que o backend recebe os POSTs. Como
 * os `add*ToPrescription` rodam em modo `silent` (sem refetch nem toast por item), a
 * prescrição é sincronizada uma única vez no fim.
 *
 * @param details Detalhes do modelo a aplicar.
 * @param selection Itens escolhidos pelo médico; `undefined` aplica tudo.
 * @param labels Rótulos já resolvidos (nomes de fórmula e conselho do prescritor).
 * @returns O que o médico precisa revisar depois e os itens que falharam.
 */
async function addProtocolItemsToPrescription(
  details: ProtocolDetail,
  selection?: ApplySelection,
  labels: { formulaNames?: Map<number, string>; prescriberConselho?: string | null } = {},
): Promise<{
  unresolvedCidCodes: string[];
  unlinkedExamCount: number;
  missingLateralityCount: number;
  failedItems: string[];
}> {
  const { formulaNames, prescriberConselho } = labels;
  const unresolvedCidCodes = new Set<string>();
  let unlinkedExamCount = 0;
  let missingLateralityCount = 0;
  const store = usePrescriptionStore.getState();
  const [cidsByCode, examsByTuss] = await Promise.all([
    resolveProtocolCids(details),
    resolveProtocolExams(
      details,
      store.prescription?.idPrescricao ?? 0,
      store.prescription?.Prescritor?.idPrescritor ?? 0
    ),
  ]);

  const includeMedication = (idx: number) => !selection || selection.medication.has(idx);
  const includeExam = (idx: number) => !selection || selection.exam.has(idx);
  const includeCertificate = (idx: number) => !selection || selection.certificate.has(idx);
  const includeReferral = (idx: number) => !selection || selection.referral.has(idx);
  const includeReport = (idx: number) => !selection || selection.report.has(idx);
  const includeFormula = (idx: number) => !selection || selection.compoundedFormula.has(idx);

  const medications = details.medications ?? [];
  /**
   * Adiciona os medicamentos do modelo preservando a ordem em que foram salvos.
   *
   * O enriquecimento de laboratório (que faz requisição por medicamento) roda em paralelo
   * antes do loop; as inclusões em si ficam sequenciais porque a ordem dos itens dentro da
   * prescrição é definida pela ordem em que o backend recebe os POSTs.
   *
   * @returns Um rótulo por medicamento que falhou, para o toast de aplicação parcial.
   */
  const applyMedications = async (): Promise<string[]> => {
    const prepared = await Promise.all(
      medications.map(async (med, i) => {
        if (!includeMedication(i) || !isCatalogMedication(med)) return null;
        return withModelGenericLabFields(protocolMedicationToMedicine(med, cidsByCode), store);
      })
    );

    const failures: string[] = [];
    for (let i = 0; i < medications.length; i++) {
      const med = medications[i];
      if (!includeMedication(i)) continue;
      // Sem o id do CID o backend recusa o medicamento inteiro; entra sem CID e o médico revisa.
      if (med.icdCode && !cidsByCode.has(med.icdCode)) unresolvedCidCodes.add(med.icdCode);
      try {
        const medicine = prepared[i];
        if (isCatalogMedication(med) && medicine) {
          await store.addMedicineToPrescription({ medicine, searchMedicineId: null }, true);

          trackGenericLabEvent(GENERIC_LAB_CLICK_EVENT, 'inclusão via modelo', medicine, {
            prescription: store.prescription,
            prescriber: store.prescriber(),
            prescriberConfiguration: store.prescriberConfiguration,
            isMobile: isMobileWeb(),
          });
        } else {
          await store.addManualMedicineToPrescription(protocolMedicationToManualMedicine(med), true);
        }
      } catch (e) {
        logger.error('addProtocolItemsToPrescription: failed adding medication', { id: med.id, error: e });
        failures.push(med.brandName ?? med.activeIngredient ?? 'Medicamento');
      }
    }
    return failures;
  };

  const flatExams: Exam[] = (details.exams ?? []).flatMap((group) =>
    protocolExamToExams(group, cidsByCode, examsByTuss)
  );
  const flatExamHasCatalog: boolean[] = (details.exams ?? []).flatMap((group) =>
    (group.items ?? []).map((item) => Boolean(item.tussCode && examsByTuss.has(item.tussCode)))
  );
  const flatExamCidCodes: string[][] = (details.exams ?? []).flatMap((group) =>
    (group.items ?? []).map((item) => protocolExamItemCidCodes(group, item))
  );
  /**
   * Adiciona os exames do modelo, contabilizando o que o médico precisa revisar depois.
   *
   * Além dos CIDs não resolvidos e dos exames sem vínculo com o catálogo, contamos os que
   * o catálogo marca como `LadoRequerido` mas o modelo não guardou lateralidade: eles
   * entram válidos na lista, porém travam a emissão até o médico preencher o lado.
   *
   * @returns Um rótulo por exame que falhou, para o toast de aplicação parcial.
   */
  const applyExams = async (): Promise<string[]> => {
    const failures: string[] = [];
    for (let i = 0; i < flatExams.length; i++) {
      if (!includeExam(i)) continue;
      for (const expectedCid of flatExamCidCodes[i] ?? []) {
        if (!cidsByCode.has(expectedCid)) unresolvedCidCodes.add(expectedCid);
      }
      if (!flatExamHasCatalog[i]) unlinkedExamCount += 1;
      if (flatExams[i]?.LadoRequerido && !flatExams[i]?.Lateralidade) missingLateralityCount += 1;
      try {
        await store.addExamToPrescription({ exam: flatExams[i], searchExamLogId: null }, true);
      } catch (e) {
        logger.error('addProtocolItemsToPrescription: failed adding exam', { index: i, error: e });
        failures.push(flatExams[i]?.Nome || 'Exame');
      }
    }
    return failures;
  };

  const certificates = details.certificates ?? [];
  const applyCertificates = async () => {
    const failures: string[] = [];
    const patient = store.prescription?.Paciente;
    const certificatePatientName = patient?.NomeSocial || patient?.Nome || '';
    for (let i = 0; i < certificates.length; i++) {
      const certificate = certificates[i];
      if (!includeCertificate(i)) continue;
      for (const code of [certificate.diagnosisCid, ...(certificate.secondaryCids ?? [])]) {
        if (code && !cidsByCode.has(code)) unresolvedCidCodes.add(code);
      }
      try {
        await store.addMedicalCertificateToPrescription(
          protocolCertificateToDTO(certificate, cidsByCode, certificatePatientName),
          true
        );
      } catch (e) {
        logger.error('addProtocolItemsToPrescription: failed adding certificate', { id: certificate.id, error: e });
        failures.push(getMedicalCertificateLabel(certificate.type, prescriberConselho));
      }
    }
    return failures;
  };

  const referrals = details.referrals ?? [];
  const applyReferrals = async () => {
    const failures: string[] = [];
    for (let i = 0; i < referrals.length; i++) {
      const referral = referrals[i];
      if (!includeReferral(i)) continue;
      if (referral.diagnosisCid && !cidsByCode.has(referral.diagnosisCid)) {
        unresolvedCidCodes.add(referral.diagnosisCid);
      }
      try {
        await store.addMedicalReferralToPrescription(protocolReferralToMedicalReferral(referral, cidsByCode), true);
      } catch (e) {
        logger.error('addProtocolItemsToPrescription: failed adding referral', { id: referral.id, error: e });
        failures.push(referral.specialty ?? 'Encaminhamento');
      }
    }
    return failures;
  };

  const reports = details.reports ?? [];
  const applyReports = async () => {
    const failures: string[] = [];
    for (let i = 0; i < reports.length; i++) {
      const report = reports[i];
      if (!includeReport(i)) continue;
      try {
        await store.addMedicalReportToPrescription(protocolReportToDTO(report), true);
      } catch (e) {
        logger.error('addProtocolItemsToPrescription: failed adding report', { id: report.id, error: e });
        failures.push(report.documentType ?? 'Relatório');
      }
    }
    return failures;
  };

  const prescription = store.prescription;
  const formulas = details.compoundedFormulas ?? [];
  const doctorId = prescription?.Prescritor?.idPrescritor;
  const formulaLabel = (index: number) => formulaNames?.get(formulas[index].formulaId) ?? 'Fórmula manipulada';

  const applyFormulas = async (): Promise<(string | null)[]> => {
    if (formulas.length === 0 || !prescription) return [];
    if (!doctorId) {
      logger.warn('addProtocolItemsToPrescription: doctorId ausente, fórmulas do modelo serão ignoradas', {
        formulasCount: formulas.length,
      });
      return formulas.map((_, i) => (includeFormula(i) ? formulaLabel(i) : null));
    }
    const failures: string[] = [];
    for (let i = 0; i < formulas.length; i++) {
      const formula = formulas[i];
      if (!includeFormula(i)) continue;
      try {
        await FormulaService.addFormulaToPrescription(prescription.idPrescricao, doctorId, formula.formulaId);
      } catch (e) {
        logger.error('addProtocolItemsToPrescription: failed adding compounded formula', {
          id: formula.id,
          formulaId: formula.formulaId,
          error: e,
        });
        failures.push(formulaLabel(i));
      }
    }
    return failures;
  };

  const results = await Promise.all([
    applyMedications(),
    applyExams(),
    applyCertificates(),
    applyReferrals(),
    applyReports(),
    applyFormulas(),
  ]);
  const failedItems = results.flat().filter((label): label is string => Boolean(label));

  const anyExamIncluded = flatExams.some((_, i) => includeExam(i));
  if (anyExamIncluded) {
    const generalCode = resolveProtocolGeneralExamCid(details.exams ?? []);
    const generalCid = generalCode ? cidsByCode.get(generalCode) ?? null : null;
    const generalObs = resolveProtocolGeneralExamObservation(details.exams ?? []);
    const { generalExamIcd, generalExamObservation } = useExamStore.getState();
    const shouldSetCid = !generalExamIcd && Boolean(generalCid);
    const shouldSetObs = !generalExamObservation && Boolean(generalObs);

    if (shouldSetCid || shouldSetObs) {
      if (shouldSetCid) useExamStore.getState().setGeneralExamIcd(generalCid);
      if (shouldSetObs) useExamStore.getState().setGeneralExamObservation(generalObs ?? '');
      useExamStore.getState().bumpGeneralExamHydration();

      const prescriptionId = usePrescriptionStore.getState().prescription?.idPrescricao;
      if (prescriptionId) {
        try {
          await ExamService.updateGeneralDataExam(prescriptionId, {
            ...(shouldSetCid && generalCid ? { CID10Estruturado: generalCid } : {}),
            Instrucoes: shouldSetObs ? generalObs ?? '' : generalExamObservation,
          });
        } catch (e) {
          logger.error('addProtocolItemsToPrescription: failed persisting general exam data', e);
        }
      }
    }
  }

  // Escrita antes do refetch, para a prescrição já vir com a observação do modelo.
  // O desfazer restaura a anterior depois do refetch, guardada em `previousObservation`.
  if (details.observation?.trim()) {
    try {
      // `silent`: a falha entra em `failedItems` e sai no toast do modelo, sem um segundo
      // toast genérico contradizendo o aviso de aplicação parcial.
      await store.onUpdatePrescriptionObservation(details.observation, true);
    } catch (e) {
      logger.error('addProtocolItemsToPrescription: failed applying observation', e);
      failedItems.push('Observação');
    }
  }

  const prescriptionId = usePrescriptionStore.getState().prescription?.idPrescricao;
  if (prescriptionId) {
    try {
      const refreshed = await PrescriptionService.fetchPrescriptionById(prescriptionId);
      usePrescriptionStore.setState((prev) => ({ ...prev, prescription: refreshed }));
    } catch (e) {
      logger.error('addProtocolItemsToPrescription: failed refreshing prescription', e);
    }
    useInteractionStore.getState().fetchMedicineInteractions(prescriptionId);
  }

  return {
    unresolvedCidCodes: Array.from(unresolvedCidCodes),
    unlinkedExamCount,
    missingLateralityCount,
    failedItems,
  };
}
